import React, { useCallback } from 'react';
import { CheckCircleIcon } from '@heroicons/react/20/solid';
import { ExclamationTriangleIcon, InformationCircleIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { XCircleIcon } from '@heroicons/react/24/outline';
import Utilities from '@util/Utilities';
import { LightBulbIcon } from '@heroicons/react/24/solid';

type Props = {
	state: 'primary' | 'info' | 'success' | 'error' | 'warning' | 'recommendation',
	title?: React.ReactNode,
	onDismiss?: () => any,
	extraClassName?: string,
	customIcon?: any,
	// true: no outer margin, for a page that spaces its blocks itself (a flex column with a gap). Default false (mb-6).
	flush?: boolean,
}

const Message: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
																																						children,
																																						state,
																																						title,
																																						onDismiss,
																																						extraClassName,
																																						customIcon,
																																						flush = false,
																																					}) => {

	const icon = useCallback(() => {
		if (!!customIcon) return customIcon;
		else
			switch (state) {
				case 'primary':
					return <CheckCircleIcon className="h-5 w-5 text-blue-400" />;
				case 'info':
					return <InformationCircleIcon className="h-5 w-5 text-sky-400" aria-hidden="true" />;
				case 'success':
					return <CheckCircleIcon className="h-5 w-5 text-green-400" aria-hidden="true" />;
				case 'error':
					return <XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />;
				case 'warning':
					return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" aria-hidden="true" />;
				case 'recommendation':
					return <LightBulbIcon className="h-5 w-5 text-cyan-400" />;

			}
	}, [state]);

	const color = useCallback(() => {
		switch (state) {
			case 'primary':
				return 'blue';
			case 'info':
				return 'sky';
			case 'success':
				return 'green';
			case 'error':
				return 'red';
			case 'warning':
				return 'yellow'
				case 'recommendation':
				return 'cyan';
		}
	}, [state]);

	return <div
		className={Utilities.classNames('rounded-md p-4', `bg-${color()}-50`, `border border-${color()}-100`, flush ? '' : 'mb-6', extraClassName)}>
		<div className="flex">
			<div className="flex-shrink-0">
				{icon()}
			</div>
			<div className={Utilities.classNames('ml-3', !title ? 'flex w-full' : '')}>
				{!!title && <h3 className={Utilities.classNames('text-sm', `text-${color()}-800`)}>
					{title}
				</h3>}
				<div
					className={Utilities.classNames('text-sm', `text-${color()}-700`, 'flex-1 md:flex md:justify-between')}>
					{children}
				</div>
			</div>
			<div className="ml-auto pl-3">
				{!!onDismiss && <div className="ml-auto pl-3">
					<div className="-mx-1.5 -my-1.5">
						<button onClick={onDismiss}
										type="button"
										className={`inline-flex rounded-md bg-${color()}-50 p-1.5 text-${color()}-700 hover:bg-${color()}-100 focus:outline-none focus:ring-2 focus:ring-${color()}-600 focus:ring-offset-2 focus:ring-offset-${color()}-50`}
						>
							<span className="sr-only">Dismiss</span>
							<XMarkIcon className="h-5 w-5" aria-hidden="true" />
						</button>
					</div>
				</div>}
			</div>
		</div>
	</div>;

};

export default Message;